import React, { useEffect, useMemo, useState } from "react"
import types from "prop-types"
import { cloneDeep } from "lodash-es"
import LazyLoad, { forceCheck } from "react-lazyload"
import { useSelector } from "react-redux"
import { subscribedToAddOnFeature } from "reduxSlices/addOnFeatureSubscriptionsSlice"
import { AUTHENTICATED_FORMS_FEATURE_NAME } from "utils/addOnFeatureHelpers"
import { ORGANIZATION_ROLES, READABLE_ROLE_NAMES, SUBMITTER_ROLE } from "utils/userRoleHelpers"
import activeBeforeInactive from "utils/activeBeforeInactive"
import { useSCIMResourceManagement } from "hooks"
import { ProfileIcon } from "shared/icons"
import ItemRow from "shared/ItemRow"
import NameFilter from "shared/NameFilter"
import withReduxProviderHOC from "shared/withReduxProvider"
import UserActiveStatusFilter from "./UserActiveStatusFilter"
import UserRoleFilter from "./UserRoleFilter"
import UserMenu from "./UserMenu"
import EmptyUsers from "./EmptyUsers"
import EmptyFilteredUsers from "./EmptyFilteredUsers"
import CreateUserButtons from "./CreateUserButtons"
import ExportUsersModal from "./ExportUsersModal"

// When the component loads, only active users will be listed
const DEFAULT_ACTIVE_STATUS_FILTER = {
  [true]: true,
  [false]: false,
}

// When the compnent loads, all roles will be listed.
// Submitters will be an option, depending on whether the organization
// is subscribed to the authenticated forms add-on feature.
const DEFAULT_ROLE_FILTER = ORGANIZATION_ROLES.reduce((filter, role) => {
  filter[role] = true
  return filter
}, {})

const defaultRoleFilter = ({ includeSubmitter }) => {
  const filter = cloneDeep(DEFAULT_ROLE_FILTER)

  if (!includeSubmitter) {
    filter[SUBMITTER_ROLE] = false
  }

  return filter
}

const checkIfActive = (user, activeStatusFilter) => activeStatusFilter[user.active] === true

const filterByRole = (user, roleFilter) => roleFilter[user.role] === true

const filterBySearch = (user, nameFilter) => {
  const matcher = new RegExp(nameFilter, "i")

  if (nameFilter) {
    return matcher.test(user.fullName)
  }

  return true
}

const UserList = ({ users }) => {
  const subscribedToAuthenticatedForms = useSelector(subscribedToAddOnFeature(AUTHENTICATED_FORMS_FEATURE_NAME))

  const { usersManagedBySCIM } = useSCIMResourceManagement()

  const [nameFilter, setNameFilter] = useState("")

  const [activeStatusFilter, setActiveStatusFilter] = useState(cloneDeep(DEFAULT_ACTIVE_STATUS_FILTER))
  const updateActiveStatusFilter = (activeStatus) => (value) => setActiveStatusFilter({ ...activeStatusFilter, [activeStatus]: value })

  const [roleFilter, setRoleFilter] = useState(defaultRoleFilter({ includeSubmitter: subscribedToAuthenticatedForms }))
  const updateRoleFilter = (role) => (value) => setRoleFilter({ ...roleFilter, [role]: value })

  const filteredUsers = useMemo(() => {
    const filtered = users.filter((user) => (
      checkIfActive(user, activeStatusFilter) && filterByRole(user, roleFilter) && filterBySearch(user, nameFilter)
    ))

    return filtered.sort(activeBeforeInactive)
  }, [activeStatusFilter, roleFilter, nameFilter, users])

  const hasFilteredUsers = filteredUsers.length > 0

  useEffect(() => {
    forceCheck()
  }, [filteredUsers])

  if (users.length === 0) return <EmptyUsers />

  return (
    <>
      {
        usersManagedBySCIM && (
          <div className="text-sm text-dark text-center space-y-4 mb-4 max-w-screen-sm mx-auto">
            <p>Your organization manages users via SCIM user provisioning.</p>
            <p>
              To add a new user or modify an existing user&apos;s email, name, or active status
              <br />
              please contact your internal support.
            </p>
          </div>
        )
      }
      <div className="flex items-center gap-x-4 mb-8 pb-6 border-b border-light-300">
        {
          !usersManagedBySCIM && (
            <CreateUserButtons />
          )
        }
        <div className="ml-auto">
          <ExportUsersModal />
        </div>
      </div>

      <div className="flex items-start justify-center gap-x-4 w-full mb-6">
        <div className="min-w-1/5 space-y-1">
          <span className="text-sm font-semibold">Active Status</span>
          <UserActiveStatusFilter
            onChange={updateActiveStatusFilter}
            value={activeStatusFilter}
          />
        </div>
        <div className="min-w-1/5 space-y-1">
          <span className="text-sm font-semibold">Role</span>
          <UserRoleFilter
            onChange={updateRoleFilter}
            value={roleFilter}
          />
        </div>
        <div className="grow space-y-1">
          <label htmlFor="user-name-filter" className="text-sm font-semibold">Name</label>
          <NameFilter
            onChange={setNameFilter}
            inputId="user-name-filter"
            value={nameFilter}
          />
        </div>
      </div>
      {
        hasFilteredUsers && (
          <div className="flex justify-end mb-2 pr-2.5 text-xs text-dark">
            Showing {filteredUsers.length}/{users.length}
          </div>
        )
      }
      <div className="admin-users-list">
        {
          hasFilteredUsers ? filteredUsers.map((user) => (
            <LazyLoad key={`user-row${user.id}`} height={64} offset={100} once resize>
              <ItemRow
                itemRowHeaderContent={(
                  <a
                    className="item-row-header-content__user-link"
                    href={`/admin/users/${user.id}`}
                  >
                    <div className="item-row-header-content__user-identity">
                      <ProfileIcon className="item-row-header-content__profile-icon" user={user} />
                      <span>{user.lastName}, {user.firstName}</span>
                    </div>
                    <span className="item-row-header-content__user-role">{READABLE_ROLE_NAMES[user.role]}</span>
                  </a>
                )}
                itemRowHeaderControls={(
                  <UserMenu user={user} />
                )}
              />
            </LazyLoad>
          )) : <EmptyFilteredUsers />
        }
      </div>
    </>
  )
}

UserList.propTypes = {
  users: types.arrayOf(types.object).isRequired,
}

export default withReduxProviderHOC(UserList)
